// F1 World Championship Dashboard Component (1950-2020)
import React, { useState, useMemo } from 'react';
import { useF1Championship } from '../../hooks/useF1Championship';
import { BarChart, LineChart, TableChart } from '../charts';

const F1ChampionshipDashboard = () => {
  const [activeView, setActiveView] = useState('overview');
  const [selectedYear, setSelectedYear] = useState(null);
  const { data, filteredData, loading, error, refetch, filters, updateFilters, clearFilters, stats } = useF1Championship();

  // Use filtered data if filters are applied, otherwise use original data
  const displayData = Object.keys(filters).length > 0 ? filteredData : data;

  // Get available years from race data
  const availableYears = useMemo(() => {
    if (!data?.races) return [];
    const years = [...new Set(data.races.map(race =>
      parseInt(race.year || (race.date && race.date.split('-')[0]))
    ))].filter(Boolean).sort((a, b) => b - a);
    return years;
  }, [data?.races]);

  const handleFilterChange = (filterType, value) => {
    updateFilters({ [filterType]: value });
  };

  const handleClearFilters = () => {
    clearFilters();
    setSelectedYear(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 theme-secondary">Loading F1 Championship data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="theme-primary text-xl mb-4">Error loading F1 data</div>
        <p className="theme-secondary mb-4">{error}</p>
        <button 
          onClick={refetch}
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:theme-bg"
        >
          Retry
        </button>
      </div>
    );
  }

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold theme-primary">Total Races</h3>
            <p className="text-2xl sm:text-3xl font-bold theme-primary">{stats.totalRaces.toLocaleString()}</p>
            <p className="text-xs sm:text-sm theme-secondary mt-1">
              {stats.dateRange.start} - {stats.dateRange.end}
            </p>
          </div>
          <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold theme-primary">Total Drivers</h3>
            <p className="text-2xl sm:text-3xl font-bold theme-primary">{stats.totalDrivers.toLocaleString()}</p>
          </div>
          <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold theme-primary">Total Circuits</h3>
            <p className="text-2xl sm:text-3xl font-bold theme-primary">{stats.totalCircuits.toLocaleString()}</p>
          </div>
          <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold theme-primary">Total Constructors</h3>
            <p className="text-2xl sm:text-3xl font-bold theme-primary">{stats.totalConstructors.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8">
        {displayData?.races && displayData.races.length > 0 && (
            <LineChart
              data={availableYears.sort((a, b) => a - b).map(year => {
                const racesInYear = displayData.races.filter(race =>
                  parseInt(race.year || (race.date && race.date.split('-')[0])) === year
                );
                return {
                  label: year.toString(),
                  value: racesInYear.length
                };
              })}
              title="Races by Year"
              subtitle="Number of races per year"
              showLabels={false}
              animate={true}
            />
        )}

        {Object.keys(filters).length > 0 && filters.year ? (
          // Year-specific view
          displayData?.drivers && displayData.drivers.length > 0 && (
            <BarChart
              data={[...displayData.drivers]
                .sort((a, b) => b.points - a.points)
                .slice(0, 8)
                .map((driver) => ({
                label: `${driver.forename} ${driver.surname}`,
                value: driver.points
              }))}
              title="Top Drivers by Points"
              subtitle={`Points for ${filters.year} season`}
              showLabels={true}
              animate={true}
            />
          )
        ) : (
          // All-time view
          stats?.topDrivers && stats.topDrivers.length > 0 && (
            <BarChart
              data={stats.topDrivers.slice(0, 10).map((driver) => ({
                label: driver.name,
                value: driver.points
              }))}
              title="Top Drivers by Points"
              subtitle="Points accumulated by top drivers over their careers"
              showLabels={true}
              animate={true}
            />
          )
        )}
      </div>
    </div>
  );

  const renderRaces = () => (
    <div className="space-y-6">
      <div className="card">
        {/* <p className="theme-secondary">Table view not available. {displayData?.races?.length || 0} races loaded.</p> */}
        <TableChart data={displayData?.races || []}
          config = {{
            columns: [
              { key: 'year', label: 'Year' },
              { key: 'round', label: 'Round' },
              { key: 'name', label: 'Race Name' },
              { key: 'circuit', label: 'Circuit' },
              { key: 'date', label: 'Date' }
            ],
            sortable: true,
            sortBy: 'year',
            sortDirection: 'desc'

          }}
        />
      </div>
    </div>
  );

  const renderDrivers = () => (
    <div className="space-y-6">
      <div className="card">
        <TableChart
          data={displayData?.drivers || []}
          config={{
            columns: [
              { key: 'driverId', label: 'Driver ID' },
              { key: 'forename', label: 'First Name' },
              { key: 'surname', label: 'Last Name' },
              { key: 'nationality', label: 'Nationality' },
              { key: 'number', label: 'Number' },
              { key: 'points', label: 'Points' }
            ],
            sortable: true,
            sortBy: 'points',
            sortDirection: 'desc'
          }}
        />
      </div>
    </div>
  );

  const renderCircuits = () => (
    <div className="space-y-6">
      <div className="card">
        <TableChart data={displayData?.circuits || []}
          config = {{
            columns: [
              { key: 'circuitId', label: 'Circuit ID' },
              { key: 'name', label: 'Circuit Name' },
              { key: 'location', label: 'Location' },
              { key: 'country', label: 'Country' },
              { key: 'raceCount', label: 'Total Races' }
            ],
            sortable: true,
            sortBy: 'raceCount',
            sortDirection: 'desc'
          }}
        />
      </div>
    </div>
  );

  const renderView = () => {
    switch (activeView) {
      case 'overview': return renderOverview();
      case 'races': return renderRaces();
      case 'drivers': return renderDrivers();
      case 'circuits': return renderCircuits();
      default: return renderOverview();
    }
  };

  return (
    <div className="f1-championship-dashboard space-y-4 sm:space-y-8 px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      {/* Header */}
      <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold theme-primary">F1 World Championship (1950-2020)</h1>
            <p className="theme-secondary mt-1 text-sm sm:text-base">
              Historical Formula 1 racing data and championship statistics
            </p>
          </div>
          <button
            onClick={refetch}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:theme-bg flex items-center text-sm sm:text-base"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="theme-bg rounded-lg shadow-md">
        <div className="border-b theme-border-secondary">
          <nav className="flex overflow-x-auto space-x-4 sm:space-x-8 px-4 sm:px-6 scrollbar-hide">
            {['overview', 'races', 'drivers', 'circuits'].map(view => (
              <button
                key={view}
                onClick={() => setActiveView(view)}
                className={`py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                  activeView === view
                    ? 'theme-primary'
                    : 'border-transparent theme-secondary hover:theme-primary hover:theme-border'
                }`}
              >
                {view.charAt(0).toUpperCase() + view.slice(1)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Filters */}
      <div className="theme-bg rounded-lg shadow-md p-4 sm:p-6">
        <h3 className="text-lg font-semibold theme-primary mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium theme-primary mb-2">Year</label>
            <select
              value={selectedYear || ''}
              onChange={(e) => {
                const year = e.target.value ? parseInt(e.target.value) : null;
                setSelectedYear(year);
                handleFilterChange('year', year);
              }}
              className="w-full px-3 py-2 border theme-border rounded-md focus:outline-none focus:ring-2 focus:ring-theme-accent text-sm sm:text-base"
            >
              <option value="">All Years</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {Object.keys(filters).length > 0 && (
            <div className="md:col-span-2 flex items-end">
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm sm:text-base"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {renderView()}
    </div>
  );
};

export default F1ChampionshipDashboard;
